// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { Resources } from '@shared/localizations/Resources';
import { LookupDropDownShowParams } from '../../UtilsClasses/LookupDropDownShowParams';
import { TerceraAccountLookupDropDownForm } from './TerceraAccountLookupDropDownForm';
import { DataCache } from '@shared/commons/DataCache';
import { TerceraLookup } from './TerceraLookup';
import { ControlsTypes } from '../../UtilsClasses/FactoryConstants';
import { AccountType } from '@shared/utils/cache/Accounts/Account.types';
import { WDSettingsUtils } from '../../UtilsClasses/WDGeneralSettingsUtils';
export class TerceraAccountLookup extends TerceraLookup {
    constructor() {
        super(...arguments);
        this.multiSelectDropDownItems = null;
    }
    getType() {
        return ControlsTypes.TerceraAccountLookup;
    }
    oninit() {
        super.oninit();
        this.ItemAll = new _AccountItemAll();
    }
    oncomplete() {
        this.repopulate();
        super.oncomplete();
        this.updateEnabledState();
    }
    updateEnabledState() {
        const ignore = this.get('ignoreForceLinkingByAccount');
        const forceLinking = DataCache.EnableForceLinkingByAccount();
        const enabled = ignore || !forceLinking;
        void this.set({ enabled });
    }
    getSelectedAccountID() {
        const acc = this.get('selectedItem');
        return acc ? acc.AcctNumber : '';
    }
    selectItem(accountId) {
        const accs = DataCache.Accounts;
        const acc = accs[accountId];
        if (!isNullOrUndefined(acc)) {
            void this.set('selectedItem', acc);
        }
    }
    moveToItem(next) {
        const newIndex = this.selectedItemIndex + (next ? 1 : -1);
        if (newIndex >= 0 && newIndex < this.items.length) {
            if (!this.showMAM && this.items[newIndex].isMAM) {
                return;
            }
            this.selectedItemIndex = newIndex;
            this.setSelectValue(this.items[newIndex]);
            this.textBox.selectAll();
        }
    }
    repopulate() {
        const accounts = this.get('onlyOwnAccounts') && DataCache.EnableForceLinkingByAccount()
            ? DataCache.getOwnedAccounts()
            : DataCache.getAccounts();
        if (DataCache.getNumberOfAccounts() === 1) {
            void this.set('visible', false);
        }
        this.multiSelectDropDownItems = null;
        const excludeMultiAsset = this.get('excludeMultiAsset');
        if (this.isMultiSelect) {
            const multiSelectItems = {};
            multiSelectItems[this.ItemAll.AcctNumber] = this.ItemAll;
            for (const key in accounts) {
                const acc = accounts[key];
                if (excludeMultiAsset && acc.AccountType !== AccountType.MultiAsset)
                    continue;
                multiSelectItems[key] = acc;
            }
            this.setItems(multiSelectItems);
            const multiSelectDropDownItems = this.items.slice();
            // Removing ItemAll from multiSelectDropDownItems;
            const itemAllIdx = multiSelectDropDownItems.indexOf(this.ItemAll);
            if (itemAllIdx !== -1) {
                multiSelectDropDownItems.splice(itemAllIdx, 1);
            }
            this.multiSelectDropDownItems = multiSelectDropDownItems;
        }
        else {
            this.setItems(accounts);
            // Default account.
            const defAcc = WDSettingsUtils.selectedTradingAccount;
            let lastItem = this.get('selectedItem');
            if (!isNullOrUndefined(lastItem) && accounts[lastItem.BstrAccount] === undefined) {
                // #84959
                lastItem = null;
            }
            if (isNullOrUndefined(lastItem) && !isNullOrUndefined(defAcc)) {
                void this.set('selectedItem', defAcc);
            }
        }
    }
    getButtonTooltip() {
        return Resources.getResource('chart.ChartPanel.accountLookup.ToolTip');
    }
    showForm(x, y) {
        super.showForm();
        const params = new LookupDropDownShowParams();
        params.x = typeof x === 'number' ? x : this.getX();
        params.y = typeof y === 'number' ? y : this.getY();
        params.items = this.isMultiSelect ? this.multiSelectDropDownItems : this.items;
        params.callBack = this.dropDownFormCallBack.bind(this);
        params.closeCallback = this.closeForm.bind(this);
        params.isMultiSelect = this.isMultiSelect;
        params.isMultiSelectMode = this.isMultiSelectMode;
        params.isMultiSelectCheckboxUsing = this.isMultiSelect;
        params.caller = this.get('parentContainerControl');
        if (this.selectedItem.multiSelected) {
            params.selectedItem = this.selectedItem.arraySelected;
        }
        else if (this.selectedItem === this.ItemAll) {
            params.selectedItem = params.items;
        }
        else {
            params.selectedItem = this.selectedItem;
        }
        params.showMAM = this.showMAM;
        void TerceraAccountLookupDropDownForm.ShowForm(params);
        this.fire('dropDownFormOpened');
    }
    getTypingLookupValueForSet(textValueId) {
        if (textValueId === 'All') {
            return this.ItemAll;
        }
        return DataCache.GetAccountById(textValueId);
    }
    dropDownFormCallBack(result) {
        const len = Array.isArray(result) ? result.length : 0;
        if (len !== 0) {
            if (len === 1) {
                this.setSelectValue(result[0]);
            }
            else if (len === DataCache.getNumberOfAccounts()) {
                this.setSelectValue(this.ItemAll);
            }
            else if (len < DataCache.getNumberOfAccounts()) {
                this.setSelectValue(new _AccountItemSelected(result));
            }
        }
        else {
            this.setSelectValue(result);
        }
    }
    get IsMultiSelect() {
        return this.isMultiSelect;
    }
    set IsMultiSelect(value) {
        this.isMultiSelect = value;
        this.repopulate();
    }
    lookupErrorHideShow(isShow) {
        super.lookupErrorHideShow(isShow);
        if (isShow) {
            this.showPopupError(Resources.getResource('TerceraAccountLookup.AccountNotExist'), Resources.getResource('TerceraAccountLookup.IncorrectAccount'));
        }
    }
}
export class _AccountItemAll {
    constructor() {
        this.AcctNumber = 'All';
        // Hide in account lookup dropdown.
        this.isHide = true;
        this.UserLogin = 'All';
        this.isLinked = false;
        this._AccountItemAll = true;
        this.AcctNumberAll = null;
    }
    toString() {
        return Resources.getResource('panel.TerceraSymbolLookupDropDownForm.All');
    }
    GetDataForSave() {
        if (isValidString(this.AcctNumberAll)) {
            return this.AcctNumberAll;
        }
        const accounts = DataCache.getAccounts();
        const keys = Object.keys(accounts);
        let FullAccString = '';
        for (let i = 0; i < keys.length; i++) {
            FullAccString += accounts[keys[i]].AcctNumber + ',';
        }
        // Removing comma.
        FullAccString = FullAccString.slice(0, -1);
        this.AcctNumberAll = FullAccString;
        return this.AcctNumberAll;
    }
}
export class _AccountItemSelected {
    constructor(accounts) {
        // Hide in account lookup dropdown.
        this.isHide = true;
        this.isLinked = false;
        this.multiSelected = true;
        this._AccountItemSelected = true;
        let id = '';
        const len = accounts ? accounts.length : 0;
        for (let i = 0; i < len; i++) {
            const acc = accounts[i];
            if (acc.AcctNumber === 'All')
                continue;
            id += accounts[i].FullAccString + ',';
        }
        // Removing comma.
        id = id.slice(0, -1);
        const itemsLocalKey = Resources.getResource('TerceraAccountLookup.AccountItems');
        this.selectedText = `${len} ${itemsLocalKey} ( ${id} )`;
        this.AcctNumber = id;
        this.arraySelected = accounts;
    }
    GetDataForSave() {
        let FullAccString = '';
        for (let i = 0; i < this.arraySelected.length; i++) {
            FullAccString += this.arraySelected[i].AcctNumber + ',';
        }
        // Removing comma.
        FullAccString = FullAccString.slice(0, -1);
        return FullAccString;
    }
    toString() {
        return this.selectedText;
    }
}
TerceraLookup.extendWith(TerceraAccountLookup, {
    data: function () {
        return {
            btnClass: 'js-lookup-showAcc',
            cssClass: 'js-tercera-lookup account-lookup',
            btnName: 'accountLookup',
            name: ControlsTypes.TerceraAccountLookup,
            onlyOwnAccounts: false, // if true, the lookup does not include linked accounts
            ignoreForceLinkingByAccount: false, // true for not disabled accounts when enable force account linking
            excludeMultiAsset: false
        };
    },
    isMultiSelect: false,
    showMAM: false,
    ItemAll: null,
    isFocusoutListenerAdded: false,
    multiSelectDropDownItems: null
});
